import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import flashcardService, { isQuotaError } from '../../services/flashcardService';
import UpgradePrompt from './UpgradePrompt';
import './Flashcards.css';

// Fixed deck color-dot palette (the one place hardcoded colors are allowed).
const COLOR_PALETTE = [
  '#6366f1',
  '#e74c3c',
  '#f39c12',
  '#22c55e',
  '#06b6d4',
  '#8b5cf6',
  '#ec4899',
  '#64748b',
];

const DeckFormModal = ({ deck, entitlement, onClose, onSaved }) => {
  const isEdit = !!deck;
  const [name, setName] = useState(deck?.name || '');
  const [description, setDescription] = useState(deck?.description || '');
  const [color, setColor] = useState(deck?.color || COLOR_PALETTE[0]);
  const [difficulty, setDifficulty] = useState(deck?.difficulty || 1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [quotaBlocked, setQuotaBlocked] = useState(false);

  const canCreate = entitlement && entitlement.canCreate;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Give your deck a name.');
      return;
    }
    setSaving(true);
    setError(null);
    const payload = {
      name: name.trim(),
      description: description.trim(),
      color,
      difficulty: Number(difficulty),
    };
    try {
      if (isEdit) {
        await flashcardService.updateDeck(deck.id, payload);
      } else {
        await flashcardService.createDeck(payload);
      }
      onSaved();
    } catch (err) {
      if (isQuotaError(err)) {
        setQuotaBlocked(true);
      } else {
        setError('Could not save the deck. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fc-modal-overlay" onClick={onClose}>
      <div
        className="fc-modal fc-modal-sm"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="fc-modal-header">
          <h2>{isEdit ? 'Edit Deck' : 'New Deck'}</h2>
          <button type="button" className="fc-modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {!canCreate || quotaBlocked ? (
          <div className="fc-modal-body">
            <UpgradePrompt
              variant="create"
              requiredPlans={entitlement?.requiredPlans}
              onDismiss={onClose}
              dismissLabel="Not now"
            />
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="fc-modal-body">
              {error && <div className="fc-error-banner">{error}</div>}

              <div className="fc-field">
                <label className="fc-label" htmlFor="fc-deck-name">
                  Name
                </label>
                <input
                  id="fc-deck-name"
                  className="fc-input"
                  type="text"
                  maxLength={100}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Cardiology High-Yield"
                  autoFocus
                />
              </div>

              <div className="fc-field">
                <label className="fc-label" htmlFor="fc-deck-desc">
                  Description
                </label>
                <textarea
                  id="fc-deck-desc"
                  className="fc-textarea"
                  maxLength={500}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What's inside this deck?"
                />
              </div>

              <div className="fc-field">
                <span className="fc-label">Color</span>
                <div className="fc-color-palette">
                  {COLOR_PALETTE.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={`fc-swatch ${color === c ? 'fc-swatch-active' : ''}`}
                      style={{ backgroundColor: c }}
                      aria-label={`Color ${c}`}
                      onClick={() => setColor(c)}
                    />
                  ))}
                </div>
              </div>

              <div className="fc-field">
                <label className="fc-label" htmlFor="fc-deck-diff">
                  Difficulty
                </label>
                <select
                  id="fc-deck-diff"
                  className="fc-select"
                  value={difficulty}
                  onChange={(e) => setDifficulty(Number(e.target.value))}
                >
                  <option value={1}>Beginner</option>
                  <option value={2}>Intermediate</option>
                  <option value={3}>Advanced</option>
                </select>
              </div>
            </div>

            <div className="fc-modal-footer">
              <button
                type="button"
                className="fc-btn fc-btn-ghost"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </button>
              <button type="submit" className="fc-btn fc-btn-primary" disabled={saving}>
                {saving && <FontAwesomeIcon icon={faSpinner} className="fc-spin" />}
                {isEdit ? 'Save Changes' : 'Create Deck'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default DeckFormModal;
